import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import {
  borderRadius,
  componentTokens,
  transitions,
  typography,
} from '../KestrixDesignSystem.jsx';
import { colorTokens } from '../tokens/colors.ts';
import type { KestrixTextInputSize } from './types.ts';

const tokens = componentTokens.input;

type StyledTextFieldProps = {
  kestrixSuccess?: boolean;
  kestrixSize?: KestrixTextInputSize;
};

/** The core MUI TextField with Kestrix design tokens applied. */
export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'kestrixSuccess' && prop !== 'kestrixSize',
})<StyledTextFieldProps>(({ kestrixSuccess = false, kestrixSize = 'default' }) => {
  const isCompact = kestrixSize === 'compact';
  const type = isCompact ? typography.body.small : typography.body.medium;
  const padding = isCompact ? tokens.padding.xs : '6px 0';
  const controlHeight = tokens.height.sm;

  return {
    width: '100%',

    '& .MuiOutlinedInput-root': {
      borderRadius: borderRadius.base,
      backgroundColor: colorTokens.semantic.brand.base.white,
      fontFamily: typography.fontFamily.primary,
      fontSize: type.fontSize,
      fontWeight: type.fontWeight,
      lineHeight: type.lineHeight,
      boxSizing: 'border-box',
      height: controlHeight,
      minHeight: controlHeight,
      paddingLeft: isCompact ? undefined : '8px',
      paddingRight: isCompact ? undefined : '8px',
      transition: `background-color ${transitions.fast}, border-color ${transitions.fast}`,

      '& fieldset': {
        borderColor: colorTokens.surface.border.subtle,
        borderWidth: 1,
        transition: `border-color ${transitions.fast}`,
      },

      '& .MuiInputAdornment-root': {
        color: colorTokens.global.neutral[400],
        '& svg': {
          width: 16,
          height: 16,
          fontSize: 16,
        },
      },

      '& .MuiInputAdornment-positionStart': {
        marginLeft: 0,
        marginRight: '6px',
      },

      '&:hover': {
        backgroundColor: colorTokens.global.neutral[500],

        '& fieldset': {
          borderColor: colorTokens.global.neutral[300],
        },
      },

      '&.Mui-focused': {
        backgroundColor: colorTokens.semantic.brand.base.white,
        '& fieldset': {
          borderColor: colorTokens.semantic.brand.primary,
          borderWidth: 1,
        },
      },

      '&.Mui-error fieldset': {
        borderColor: colorTokens.semantic.status.error,
      },

      '&.Mui-error.Mui-focused fieldset': {
        borderColor: colorTokens.semantic.status.error,
      },

      ...(kestrixSuccess
        ? {
            '& fieldset': {
              borderColor: colorTokens.semantic.status.successHighEmphasis.border,
              borderWidth: 1,
            },
            '&.Mui-focused fieldset': {
              borderColor: colorTokens.semantic.status.successHighEmphasis.border,
            },
          }
        : {}),

      '&.Mui-disabled': {
        backgroundColor: colorTokens.global.neutral[500],

        '& fieldset': {
          borderColor: colorTokens.global.neutral[600],
        },
      },
    },

    '& .MuiOutlinedInput-input': {
      padding,
      color: colorTokens.text.bodyNeutral,
      fontFamily: typography.fontFamily.primary,
      fontSize: type.fontSize,
      fontWeight: type.fontWeight,
      lineHeight: type.lineHeight,

      '&::placeholder': {
        color: colorTokens.global.neutral[400],
        opacity: 1,
      },

      '&.Mui-disabled': {
        color: colorTokens.text.disabled,
        WebkitTextFillColor: colorTokens.text.disabled,
      },
    },

    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
      height: 'auto',
      minHeight: 'unset',
      padding: 0,
      alignItems: 'flex-start',
      overflow: 'hidden',
    },

    '& .MuiOutlinedInput-inputMultiline': {
      padding: isCompact ? tokens.padding.xs : '6px 8px',
      overflowY: 'auto',
    },
  };
});
